import React from "react";
import RequestEngine from "../../core/RequestEngine";
import CoreEngine from "../../core/CoreEngine";
import FormPage from "../../components/Form/FormPage";
import strings from "../../core/translate";
import { apiErrorMessage } from "../../shared/api-error";
import MerchantWorkerForm from "./MerchantWorkerAddUdateForm";

export default class MerchantWorkerAddUpdate extends CoreEngine {
  constructor(props) {
    super(props);
    this.state = {
      isLoading: false,
      id: "",
      name: "",
      title: "",
      phoneNumber: "",
      username: "",
      password: "",
      selectedBranch: "",
      branchName: "",
      branchFixed: false,
      canAccessApp: true,
      // a new member of staff starts as a Mechanic: app only
      role: "mechanic",
      tierId: "",
      // the selected garage's tiers
      tiers: [],
      branches: [],
      loading: false,
      error: "",
      errors: {},
    };
    this.engine = new RequestEngine();
  }

  async componentDidMount() {
    const { selectedMerchant } = this.props.match.params;
    const routeState = this.props.location.state || {};
    this.pathname = `merchants/${selectedMerchant}/workers`;

    // opened from a garage, or by a garage login: the garage is already known
    const garageBranchId =
      routeState.selectedBranch || sessionStorage.getItem("garageBranchId");

    const edit = this.props.location.data;
    if (edit) {
      this.setState({
        id: edit.id,
        name: edit.name || "",
        title: edit.title || "",
        phoneNumber: edit.phoneNumber || "",
        username: edit.username || "",
        selectedBranch: edit.branch ? edit.branch.id : garageBranchId || "",
        branchName: edit.branch ? edit.branch.name || edit.branch.address : "",
        canAccessApp: edit.canAccessApp ?? true,
        role: edit.role || "mechanic",
        tierId: edit.tier ? edit.tier.id : "",
        version: edit.version,
      });
    }

    if (garageBranchId) {
      this.setState({
        branchFixed: true,
        selectedBranch: garageBranchId,
        branchName: routeState.branchName || this.state.branchName,
      });
      if (!routeState.branchName) {
        this.fetchBranchName(garageBranchId);
      }
      this.fetchTiers(garageBranchId);
      return;
    }

    await this.fetchBranches(selectedMerchant);
    if (this.state.selectedBranch) this.fetchTiers(this.state.selectedBranch);
  }

  // the tiers belong to a garage, so they follow the garage picked
  fetchTiers = async (branchId) => {
    if (!branchId) {
      this.setState({ tiers: [] });
      return;
    }
    try {
      const response = await this.engine.getItem(
        `merchants-branches/${branchId}/tiers`
      );
      const tiers = Array.isArray(response && response.data)
        ? response.data
        : [];
      this.setState((prev) => ({
        tiers,
        // a tier from another garage cannot be kept
        tierId: tiers.some((t) => t.id === prev.tierId) ? prev.tierId : "",
      }));
    } catch (err) {
      this.setState({ tiers: [] });
    }
  };

  fetchBranchName = async (branchId) => {
    try {
      const response = await this.engine.getItem(
        `merchants-branches/${branchId}`
      );
      const branch = response && response.data;
      if (branch) {
        this.setState({ branchName: branch.name || branch.address || "" });
      }
    } catch (err) {
      // the name is decoration here, the id is what gets saved
    }
  };

  fetchBranches = async (merchantId) => {
    try {
      const response = await this.engine.getItem(
        `merchants/${merchantId}/branches`
      );
      if (response && response.status === 200) {
        this.setState({
          branches: response.data.items.map((b) => ({
            value: b.id,
            label: b.name || b.address,
          })),
        });
      }
    } catch (err) {
      // an empty picker is better than a broken form
    }
  };

  // mirrors what the api enforces on a worker
  validate() {
    const {
      id,
      name,
      title,
      phoneNumber,
      username,
      password,
      selectedBranch,
      branchFixed,
    } = this.state;
    const errors = {};

    if (`${name}`.trim().length < 3) {
      errors.name = strings.name_min3_hint;
    }
    if (`${title}`.trim().length < 3) {
      errors.title = strings.occupation_hint;
    }
    if (!/^[0-9+()\s-]{6,20}$/.test(`${phoneNumber}`.trim())) {
      errors.phoneNumber = strings.phone_hint;
    }
    // the username is optional, but a typed one has to be usable at sign-in
    const typedUsername = `${username}`.trim();
    if (typedUsername && !/^[a-zA-Z0-9._-]{3,30}$/.test(typedUsername)) {
      errors.username = strings.username_format_hint;
    }
    if (!id && `${password}`.length < 6) {
      errors.password = strings.worker_password_hint;
    }
    if (id && password && `${password}`.length < 6) {
      errors.password = strings.worker_password_hint;
    }
    if (!branchFixed && !selectedBranch) {
      errors.selectedBranch = `${strings.select} ${strings.garage}`;
    }
    return errors;
  }

  handleValidSubmit = async (event) => {
    if (event) event.preventDefault();

    const errors = this.validate();
    if (Object.keys(errors).length > 0) {
      this.setState({ errors });
      return;
    }

    this.setState({ loading: true, error: "", errors: {} });
    const {
      id,
      name,
      title,
      phoneNumber,
      username,
      password,
      selectedBranch,
      canAccessApp,
      role,
      tierId,
      version,
    } = this.state;

    const trimmedUsername = `${username}`.trim();
    const data = {
      name: `${name}`.trim(),
      title: `${title}`.trim(),
      phoneNumber: `${phoneNumber}`.trim(),
      canAccessApp,
      role,
      // on edit "no tier" has to be said, or the old one would stay
      ...(id ? { tierId: tierId || null } : tierId && { tierId }),
      // on edit an emptied box clears the username, so send it either way
      ...(id
        ? { username: trimmedUsername || null }
        : trimmedUsername && { username: trimmedUsername }),
      ...(password && { password }),
      ...(selectedBranch && { branchId: selectedBranch }),
      ...(id && { version }),
    };

    try {
      const response = id
        ? await this.engine.updateItem(this.pathname, id, data)
        : await this.engine.saveItem(this.pathname, data);

      if (response && (response.status === 201 || response.status === 200)) {
        this.setState({ loading: false });
        this.goBack();
        return;
      }
    } catch (err) {
      this.setState({ error: apiErrorMessage(err) });
    }
    this.setState({ loading: false });
  };

  goBack = () => {
    const { selectedMerchant } = this.props.match.params;
    const { branchFixed, selectedBranch, branchName } = this.state;
    if (branchFixed && selectedBranch) {
      this.props.history.push({
        pathname: `/admin/merchants-branches/${selectedBranch}/workers`,
        state: { selectedMerchant, branchName },
      });
      return;
    }
    this.props.history.push(`/admin/merchant/${selectedMerchant}/workers`);
  };

  handleFieldChange = (field, value) => {
    this.setState((prev) => ({
      [field]: value,
      errors: { ...prev.errors, [field]: undefined },
    }));
  };

  handleBranchSelect = (value) => {
    this.setState((prev) => ({
      selectedBranch: value,
      errors: { ...prev.errors, selectedBranch: undefined },
    }));
    this.fetchTiers(value);
  };

  render() {
    const {
      loading,
      isLoading,
      name,
      title,
      phoneNumber,
      username,
      password,
      selectedBranch,
      branchName,
      branchFixed,
      canAccessApp,
      role,
      tierId,
      tiers,
      branches,
      id,
      error,
      errors,
    } = this.state;

    if (isLoading) return this.renderProgress();

    return (
      <FormPage
        title={
          id
            ? `${strings.edit} ${strings.merchant_workers}`
            : `${strings.add} ${strings.merchant_workers}`
        }
        subtitle={branchFixed ? branchName : ""}
      >
        <MerchantWorkerForm
          name={name}
          title={title}
          phoneNumber={phoneNumber}
          username={username}
          password={password}
          selectedBranch={selectedBranch}
          branchName={branchName}
          branchFixed={branchFixed}
          canAccessApp={canAccessApp}
          role={role}
          tierId={tierId}
          tiers={tiers}
          branches={branches}
          isEdit={!!id}
          loading={loading}
          error={error}
          errors={errors}
          handleValidSubmit={this.handleValidSubmit}
          handleFieldChange={this.handleFieldChange}
          handleBranchSelect={this.handleBranchSelect}
          onCancel={this.goBack}
        />
      </FormPage>
    );
  }
}
